import React, { useEffect, useState } from 'react';
import PaymentTrendsGraph from './PaymentTrendsGraph';
import { PaymentTrendsDateFilterTypes, PaymentTrendsPaymentTypes } from '../../config/FilterOptionTypes';
import { getEndOfDay, getPastXDays, toIsoDateString, DateRange } from "../../util/DateUtility";
import { getTrends } from '../../service/PaymentTrendsService';
import DateFilter from '../DateFilter/DateFilter';
import { useCompany } from '../../component/CompanySelection/CompanyContext';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import PaymentTrendsScoreboard from './PaymentTrendsScoreboard/PaymentTrendsScoreboard';
import PaymentTypeFilter from '../PaymentTypeFitler/PaymentTypeFilter';
import './PaymentTrends.css'

export const TRENDS_CACHE_KEY = 'sevenDayAllGraph';

export default function PaymentTrends() {
    const [graphData, setGraphData] = useState([]);
    const [legendData, setLegendData] = useState([]);
    const [startDate, setStartDate] = useState(getDefaultStartDate());
    const [endDate, setEndDate] = useState(getDefaultEndDate());
    const [dateRange, setDateRange] = useState(getDateRangeDefault());
    const [paymentType, setPaymentType] = useState(getPaymentTypeDefault());
    const [hasError, setHasError] = useState(false);
    const { companyNumber } = useCompany();
    const [isLoading, setIsLoading] = useState(false);
    const [dropdownSelection, setDropdownSelection] = useState(0);
    const [lastDateFilterType, setLastDateFilterType] = useState('');

    function dataHandler(data) {
        setGraphData(sortGraphData(data.graphData));
        setLegendData(sortLegendData(data.legendData));
    };

    function sortGraphData(data) {
        let sortedData = [];
        if (data && data.length > 0) {
            sortedData = [data.shift(), ...sortLegendData(data)]
        }

        return sortedData;
    }

    function sortLegendData(data) {
        let sortedList = [];
        if (data) {
            // Sort Alphabetically 
            sortedList = data.sort((a, b) => (a[0]?.toLowerCase() > b[0]?.toLowerCase()) ? 1 : -1)
        }

        return sortedList;
    }

    function errorHandler(error) {
        if (error && error.response.status === 404) {
            setHasError(false);
            // Set data to blank to avoid confusion.
            setGraphData([]);
            setLegendData([]);
        } else {
            setHasError(true);
        }
        setIsLoading(false);
    };

    function getDefaultStartDate() {
        const currentDate = getEndOfDay();
    
        let beginningDate = new Date(currentDate);
        beginningDate.setDate(beginningDate.getDate() - 7);

        return toIsoDateString(beginningDate);
    }

    function getDefaultEndDate() {
        const currentDate = getEndOfDay();
        return toIsoDateString(currentDate);
    }

    function getPaymentTypeDefault() {
        // ALL
        return PaymentTrendsPaymentTypes[0];
    }

    function getDateRangeDefault() {
        // 7 days
        return PaymentTrendsDateFilterTypes[0];
    }

    useEffect(() => {
        // if default selected (7 days, all types), retrieve from cache
        if (dateRange === getDateRangeDefault() && paymentType === getPaymentTypeDefault() && window.sessionStorage.getItem(TRENDS_CACHE_KEY)) {
            dataHandler(JSON.parse(window.sessionStorage.getItem(TRENDS_CACHE_KEY)));
        } else if (startDate && endDate) {
            setIsLoading(true);
            if(lastDateFilterType === 'custom-date-range'){
                //Adding a date to the end date so it gets all of the items for the end date selected.
                let newEndDat = new Date(endDate);
                newEndDat.setDate(newEndDat.getDate() + 1);
                (async () => await getTrends(errorHandler, companyNumber, paymentType.value, startDate, toIsoDateString(newEndDat)))()
                .then(data => {
                    if (data) {
                        dataHandler(data);
                    }
                    setIsLoading(false);
                });
            } else {
                (async () => await getTrends(errorHandler, companyNumber, paymentType.value, startDate, endDate))()
                    .then(data => {
                        if (data) {
                            if (dateRange === getDateRangeDefault() && paymentType === getPaymentTypeDefault()) {
                                window.sessionStorage.setItem(TRENDS_CACHE_KEY, JSON.stringify(data))
                                dataHandler(JSON.parse(window.sessionStorage.getItem(TRENDS_CACHE_KEY)));
                            } else {
                                dataHandler(data);
                            }
                        }
                        setIsLoading(false);
                    });
            }
        }
    }, [startDate, endDate, paymentType,companyNumber]);     // eslint-disable-line react-hooks/exhaustive-deps

    function handleDateChange(type, startDate, endDate) {
        let dateRange;
        switch (type) {
            case 'last7days':
                setDateRange(PaymentTrendsDateFilterTypes[0]);
                dateRange = getPastXDays(7);
                setDropdownSelection(0);
                break;
            case 'last30days':
                setDateRange(PaymentTrendsDateFilterTypes[1]);
                dateRange = getPastXDays(30);
                setDropdownSelection(1);
                break;
            case 'last60days':
                setDateRange(PaymentTrendsDateFilterTypes[2]);
                dateRange = getPastXDays(60);
                setDropdownSelection(2);
                break;
            case 'last90days':
                setDateRange(PaymentTrendsDateFilterTypes[3]);
                dateRange = getPastXDays(90);
                setDropdownSelection(3);
                break;
            case 'custom-date-range':
                setDateRange(PaymentTrendsDateFilterTypes[4]);
                // start out with custom start and end dates not set
                if (lastDateFilterType === 'custom-date-range' && startDate && endDate) {
                    dateRange = new DateRange(startDate, endDate)
                } else {
                    setStartDate(null);
                    setEndDate(null);
                }   
                setDropdownSelection(4);
                break; 
            default:
                // Revert back to default is no valid option was selected
                if (lastDateFilterType === 'custom-date-range' && (startDate && endDate)) {
                    setDateRange(PaymentTrendsDateFilterTypes[4]);
                    dateRange = new DateRange(startDate, endDate)
                    
                } else {
                    setDateRange(getDateRangeDefault());
                    dateRange = getPastXDays(7);
                }
        }
        setLastDateFilterType(type)
        if (dateRange) {
            const rangeStart = toIsoDateString(dateRange.startOn);
            const rangeEnd = toIsoDateString(dateRange.endOn);
            setStartDate(rangeStart);
            setEndDate(rangeEnd);
        } else {
            setStartDate(null);
            setEndDate(null);
        }
    }

    function handlePaymentTypeChange(e)  {
        setPaymentType(e);
    }

    //callback for child components
    function setStartAndEndDates(start, end) {
        setStartDate(toIsoDateString(start))
        setEndDate(toIsoDateString(end))
    }

    return (
        
        <div>
            <div className="row">
                <div className="col">
                    <div className="sectionTitle" data-testid="payment-trends-label">Payment Trends</div>
                </div>
            </div>
            {hasError ?
                <div className="row dashBox" data-testid="payment-trends-unavailable">
                    <h3>Payment Trends is unavailable</h3>
                </div>
                :
                <div className="row dashBox">
                    {isLoading ? <LoadingSpinner blue testId='payment-trends-loading-spinner' /> : <>
                        <div className="col-9">
                            <PaymentTrendsGraph data={graphData} legendData={legendData} />
                        </div>
                        <div className="col-3 efup-payment-trends-side-content">
                            <div className='efup-payment-trends-date-select' >
                                <DateFilter onChange={handleDateChange} options={PaymentTrendsDateFilterTypes} startDate={startDate ? new Date(startDate.replace(/-/g, '/')) : null} endDate={endDate ? new Date(endDate.replace(/-/g, '/')) : null}  setParentDates={setStartAndEndDates} lastDateFilterType={lastDateFilterType} defaultValue={PaymentTrendsDateFilterTypes[dropdownSelection]} filterLabel={"Date:"}
                                />
                            </div>
                            <PaymentTrendsScoreboard data={legendData} />
                        </div>
                    </>
                    }
                </div >
            }
        </div >
    )
}